import { useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { useI18n } from "@wato/i18n/react";
import { Alert, Button, Input, Stack } from "@wato/ui";
import { useApi, useErrorMessage } from "../../api/context";
import { AuthLayout } from "../../components/common";

/** The reset link carries the token in the URL fragment (`#token=…`, ADR-0013). */
function tokenFromHash(hash: string): string | null {
  const token = new URLSearchParams(hash.replace(/^#/, "")).get("token");
  return token && token.length > 0 ? token : null;
}

export function ResetPasswordPage() {
  const location = useLocation();
  const token = tokenFromHash(location.hash);
  return token ? <ConfirmReset token={token} /> : <RequestReset />;
}

function RequestReset() {
  const { t } = useI18n();
  const api = useApi();
  const errorMessage = useErrorMessage();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.requestPasswordReset(email.trim());
      setDone(true);
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout
      title={t("dashboard.auth.reset.requestTitle")}
      subtitle={t("dashboard.auth.reset.requestBody")}
      footer={
        <Link className="font-semibold text-on-action-ghost underline" to="/login">
          {t("dashboard.auth.backToLogin")}
        </Link>
      }
    >
      <form onSubmit={submit} noValidate>
        <Stack gap={4}>
          {done && <Alert tone="success">{t("dashboard.auth.reset.requestDone")}</Alert>}
          {error && <Alert tone="error">{error}</Alert>}
          <Input
            type="email"
            dir="ltr"
            autoComplete="email"
            required
            label={t("dashboard.auth.email")}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <Button type="submit" fullWidth loading={busy}>
            {t("dashboard.auth.reset.requestSubmit")}
          </Button>
        </Stack>
      </form>
    </AuthLayout>
  );
}

function ConfirmReset({ token }: { token: string }) {
  const { t } = useI18n();
  const api = useApi();
  const navigate = useNavigate();
  const errorMessage = useErrorMessage();
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.confirmPasswordReset(token, password);
      // Drop the token from the address bar/history before leaving.
      await navigate("/login?passwordChanged=1", { replace: true });
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout
      title={t("dashboard.auth.reset.confirmTitle")}
      subtitle={t("dashboard.auth.reset.signedOutEverywhere")}
    >
      <form onSubmit={submit} noValidate>
        <Stack gap={4}>
          {error && <Alert tone="error">{error}</Alert>}
          <Input
            type="password"
            autoComplete="new-password"
            required
            minLength={10}
            label={t("dashboard.auth.reset.newPassword")}
            hint={t("dashboard.auth.signup.passwordHint")}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <Button type="submit" fullWidth loading={busy}>
            {t("dashboard.auth.reset.confirmSubmit")}
          </Button>
        </Stack>
      </form>
    </AuthLayout>
  );
}
